import { Coins } from "lucide-react";
import { useLayoutEffect, useRef, type CSSProperties } from "react";

import { familyById } from "../themes/families";
import { familyTokens } from "../themes/resolver";
import classes from "./case.module.css";
import { EASING, SPIN_MS, WINNER_AT, cardAtMarker, landingOffset, type ReelCard } from "./reel";

/**
 * A card's look: a theme's dark variant as a tiny app, or the jackpot with what it pays, as the
 * server says it (coins when it says nothing).
 */
function Card({ card, winner, jackpot }: { card: ReelCard; winner: boolean; jackpot: number | null }) {
  const rarity = { "--rarity": `var(--cai-rarity-${card.tier})` } as CSSProperties;
  // The server's card, the one the reel stops on, or rests on once today's case is opened.
  const mark = winner ? "" : undefined;
  if (card.id === "jackpot") {
    return (
      <span className={classes.reelCard} data-jackpot data-winner={mark} style={rarity}>
        <span className={classes.prize}>
          {jackpot === null ? <Coins size={26} aria-hidden /> : jackpot.toLocaleString("en-US")}
          <small>free credits</small>
        </span>
        <span className={classes.reelName}>Jackpot</span>
        <span className={classes.rarityBar} />
      </span>
    );
  }
  const family = familyById(card.id);
  const tokens = familyTokens(family, "dark");
  return (
    <span className={classes.reelCard} data-winner={mark} style={rarity}>
      <span className={classes.reelPreview} style={{ background: tokens["--cai-color-page"] }}>
        <span style={{ background: tokens["--cai-color-surface"] }} />
        <span style={{ background: tokens["--cai-color-primary"] }} />
      </span>
      <span className={classes.reelName}>{family.name}</span>
      <span className={classes.rarityBar} />
    </span>
  );
}

const reducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

/**
 * The strip of cards sliding past the centre marker to the server's card, CS:GO's way: fast, then
 * a long settle, a tick per card that passes. With reduced motion, or a browser without the Web
 * Animations API, it stands on the winner at once. `spinKey` starts a spin; Skip lands at once.
 * `standing` rests it on its winner with no spin at all (`data-rest`): today's case, opened before.
 */
export function CaseReel({
  cards,
  jackpot,
  spinKey,
  standing,
  skipped,
  onTick,
  onLanded,
}: {
  cards: readonly ReelCard[];
  /** What the jackpot pays, from the server; null when it says nothing. */
  jackpot: number | null;
  spinKey: number;
  /** At rest on the strip's winner, under the marker: no spin, no tick and no landing. */
  standing: boolean;
  skipped: boolean;
  onTick: () => void;
  onLanded: (winner: DOMRect | undefined) => void;
}) {
  const view = useRef<HTMLDivElement>(null);
  const strip = useRef<HTMLDivElement>(null);
  const animation = useRef<Animation | null>(null);
  // The latest callbacks, set before the spin's effect below runs, so a landing tells them.
  const handlers = useRef({ onTick, onLanded });
  useLayoutEffect(() => {
    handlers.current = { onTick, onLanded };
  });

  useLayoutEffect(() => {
    const viewport = view.current;
    const row = strip.current;
    if (spinKey === 0 || viewport === null || row === null) return;
    const first = row.children[0] as HTMLElement | undefined;
    const card = first?.getBoundingClientRect().width ?? 0;
    const gap = Number.parseFloat(getComputedStyle(row).columnGap) || 0;
    const step = card + gap;
    const width = viewport.clientWidth;
    const end = landingOffset(width, card, step);
    const land = () => {
      row.style.transform = `translateX(${end}px)`;
      handlers.current.onLanded((row.children[WINNER_AT] as HTMLElement | undefined)?.getBoundingClientRect());
    };
    if (reducedMotion() || typeof row.animate !== "function") {
      land();
      return;
    }
    const spin = row.animate([{ transform: "translateX(0px)" }, { transform: `translateX(${end}px)` }], {
      duration: SPIN_MS,
      easing: `cubic-bezier(${EASING.join(",")})`,
      fill: "forwards",
    });
    animation.current = spin;
    const start = performance.now();
    let last = -1;
    let frame = 0;
    const watch = (now: number) => {
      const progress = Math.min(1, (now - start) / SPIN_MS);
      const passing = cardAtMarker(progress, width, end, step);
      if (passing !== last) {
        if (last !== -1) handlers.current.onTick();
        last = passing;
      }
      if (progress < 1) frame = requestAnimationFrame(watch);
    };
    frame = requestAnimationFrame(watch);
    spin.finished.then(
      () => {
        cancelAnimationFrame(frame);
        spin.commitStyles();
        spin.cancel();
        land();
      },
      () => cancelAnimationFrame(frame),
    );
    return () => {
      cancelAnimationFrame(frame);
      spin.cancel();
    };
  }, [spinKey]);

  // Skip: the spin jumps to its end, which lands it.
  useLayoutEffect(() => {
    if (skipped) animation.current?.finish();
  }, [skipped]);

  // At rest on today's prize (spec web/case-reopen-prize): its centre under the marker, with no
  // jitter, and less the strip's left padding, which a spin's jitter hides. Layout widths, not drawn
  // ones, as the dialog may still be scaling in. The marker keeps to the reel's middle, so the prize
  // stands again whenever the reel's width changes, as a narrower window narrows the dialog.
  useLayoutEffect(() => {
    const viewport = view.current;
    const row = strip.current;
    if (!standing || spinKey > 0 || viewport === null || row === null) return;
    const stand = () => {
      const card = (row.children[0] as HTMLElement | undefined)?.offsetWidth ?? 0;
      const style = getComputedStyle(row);
      const step = card + (Number.parseFloat(style.columnGap) || 0);
      const lead = Number.parseFloat(style.paddingLeft) || 0;
      row.style.transform = `translateX(${landingOffset(viewport.clientWidth, card, step, () => 0.5) - lead}px)`;
    };
    stand();
    const resized = new ResizeObserver(stand);
    resized.observe(viewport);
    return () => resized.disconnect();
  }, [standing, spinKey, cards]);

  return (
    <div className={classes.reel} ref={view} aria-hidden data-reel data-rest={standing && spinKey === 0 ? "" : undefined}>
      <div className={classes.strip} ref={strip}>
        {cards.map((card, at) => (
          <Card key={at} card={card} winner={(spinKey > 0 || standing) && at === WINNER_AT} jackpot={jackpot} />
        ))}
      </div>
      <span className={classes.marker} />
    </div>
  );
}
